import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Projects and environments, not one big bucket',
    description:
      'Secrets live in projects, environments, and folders: versioned, referenceable, and reversible. Dev, staging, and prod stop sharing one namespace.',
    bullets: [
      'Project / environment / folder hierarchy',
      'Versioning with one-click rollback',
      'Secret references: define once, reuse everywhere',
      'Share and import flows between teams',
    ],
  },
  {
    title: 'Dynamic secrets that expire themselves',
    description:
      'Databases and clusters get short-lived credentials minted on demand. When the lease ends, the credential dies: nothing standing to steal.',
    bullets: [
      'Dynamic credentials for PostgreSQL, MSSQL, MySQL, Kubernetes',
      'Short-lived leases with automatic revocation',
      'No standing database passwords in config files',
      'Every mint and revoke in the audit trail',
    ],
  },
  {
    title: 'Machine identities for your workloads',
    description:
      'Services and pipelines authenticate with their own identities: static keys where you need them, OIDC-bound claims where you can do better.',
    bullets: [
      'Universal machine identity keys for services',
      'OIDC/JWKS bound-claim authentication for CI/CD',
      'Scoped access per project and environment',
      'REST API and a native CLI for automation',
    ],
  },
  {
    title: 'Sync out, stay the source of truth',
    description:
      'The vault pushes and rotates secrets into the stores your platforms already read (AWS, Azure, GCP, Kubernetes), while policy and audit stay in one place.',
    bullets: [
      'Sync hub to AWS, Azure, GCP, and Kubernetes secrets',
      'Rotation propagates to every synced target',
      'Monopam vault remains the single source of truth',
      'PAM-grade approval, policy, and audit on top',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Create a project',
    description:
      'Model your app as projects and environments; import existing secrets or define them fresh, with references.',
  },
  {
    title: 'Wire the workloads',
    description:
      'Services authenticate with machine identities via the CLI, the API, or OIDC-bound claims from your CI/CD.',
  },
  {
    title: 'Rotate and sync',
    description:
      'Dynamic secrets expire on lease; static ones rotate and sync to your cloud secret stores automatically.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Credential vault',
    description:
      'The same vault that guards privileged accounts guards these.',
    href: '/pam/vault',
  },
  {
    label: 'Use case: DevOps privileged access',
    description:
      'Clusters, databases, servers, and secrets: one brokered model.',
    href: '/solutions/devops-privileged-access',
  },
  {
    label: 'What is secrets management?',
    description:
      'The concept, explained in the glossary.',
    href: '/glossary/secrets-management',
  },
]

export default function SecretsManagementPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Developer secrets management"
        description="A developer secrets platform inside your PAM: projects and environments with versioning, dynamic database and Kubernetes credentials with auto-expiring leases, machine identities with OIDC-bound claims, a native CLI, and sync to AWS, Azure, GCP, and Kubernetes."
        path="/pam/secrets-management"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monopam · Secrets Management"
          title={
            <>
              Secrets for developers.
              <br />
              Governance for security.
            </>
          }
          body="API keys in env vars, database passwords in CI variables, tokens in dotfiles: developer secrets sprawl until they leak. Monopam gives engineering a secrets platform they actually want to use, inside the vault your security team already governs."
          badges={[
            'Dynamic secrets with auto-expiring leases',
            'CLI + API native',
            'Syncs to AWS · Azure · GCP · K8s',
          ]}
        />
        <SolutionPillars
          title={<>A secrets platform, not a password box.</>}
          body="Point secrets tools give developers convenience and security teams a blind spot. Monopam keeps both: developer-grade ergonomics on a PAM-grade vault."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>From sprawl to source of truth in three steps.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
